/* eslint-disable react/require-default-props */
import { uniqueId } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Message } from "semantic-ui-react";
import { MESSAGE_CENTER_DISMISS_AFTER_MILLIS } from "./index";

export interface Props {
  errorMessages?: string[];
  infoMessages?: string[];
  successMessages?: string[];
  dismissable?: boolean;
  compact?: boolean;
  autoDismiss?: boolean;
  onDismissErrors?: () => void;
}

export const MessageCenter = ({
  errorMessages = [],
  infoMessages = [],
  successMessages = [],
  dismissable = true,
  compact = false,
  autoDismiss = true,
  onDismissErrors = () => undefined,
}: Props) => {
  const [errorVisible, setErrorVisible] = useState(true);
  const [successVisible, setSuccessVisible] = useState(true);
  const [infoVisible, setInfoVisible] = useState(true);
  const messageCenterRef = useRef<HTMLDivElement>(null);

  const getTimedDismiss = useCallback(
    (dismissCallback: () => void) => {
      return setTimeout(() => {
        if (autoDismiss) {
          dismissCallback();
        }
      }, MESSAGE_CENTER_DISMISS_AFTER_MILLIS);
    },
    [autoDismiss]
  );

  useEffect(() => {
    setErrorVisible(true);
    const timedDismiss = getTimedDismiss(dismissErrors);
    return () => {
      clearTimeout(timedDismiss);
    };
    // eslint-disable-next-line
  }, [errorMessages, autoDismiss, getTimedDismiss]);

  useEffect(() => {
    setSuccessVisible(true);
    const timedDismiss = getTimedDismiss(() => setSuccessVisible(false));
    return () => {
      clearTimeout(timedDismiss);
    };
  }, [successMessages, autoDismiss, getTimedDismiss]);

  useEffect(() => {
    setInfoVisible(true);
    const timedDismiss = getTimedDismiss(() => setInfoVisible(false));
    return () => {
      clearTimeout(timedDismiss);
    };
  }, [infoMessages, autoDismiss, getTimedDismiss]);

  useEffect(() => {
    if (
      messageCenterRef.current &&
      (successVisible || errorVisible || infoVisible)
    ) {
      messageCenterRef.current.scrollIntoView(true);
    }
  }, [successVisible, errorVisible, infoVisible]);

  const dismissErrors = () => {
    setErrorVisible(false);
    onDismissErrors();
  };

  return (
    <div className="message-center" ref={messageCenterRef}>
      {successMessages?.length > 0 && (
        <Message
          compact={compact}
          success
          hidden={!successVisible}
          onDismiss={dismissable ? () => setSuccessVisible(false) : undefined}
        >
          <Message.Header>Success</Message.Header>
          <Message.Content>
            {successMessages.map((message, index) => (
              <p key={uniqueId(`successmsg-${index}`)}>{message}</p>
            ))}
          </Message.Content>
        </Message>
      )}
      {errorMessages?.length > 0 && (
        <Message
          compact={compact}
          error
          hidden={!errorVisible}
          onDismiss={dismissable ? dismissErrors : undefined}
        >
          <Message.Header>Error</Message.Header>
          <Message.Content>
            {errorMessages.map((message, index) => (
              <p key={`errmsg-${index}`}>{message}</p>
            ))}
          </Message.Content>
        </Message>
      )}
      {infoMessages?.length > 0 && (
        <Message compact={compact} info hidden={!infoVisible}>
          <Message.Content>
            {infoMessages.map((message, index) => (
              <p key={`infomsg-${index}`}>{message}</p>
            ))}
          </Message.Content>
        </Message>
      )}
    </div>
  );
};
